import {
  Stack,
  Group,
  Button,
  Table,
  TextInput,
  Card,
  Modal,
  NumberInput,
  Alert,
  Text,
  Badge,
  Tooltip,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconTruck,
  IconPencil,
  IconTrash,
  IconPower,
  IconChevronDown,
} from '@tabler/icons-react';
import {
  useVehiculos,
  useCreateVehiculo,
  useUpdateVehiculo,
  useDeleteVehiculo,
  getErrorMessage,
} from '../../api/hooks';
import type { CreateVehiculoDto, UpdateVehiculoDto, Vehiculo } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Valida que la matrícula del tractor encaje con el formato nacional
 * nuevo (NNNN-LLL) o el formato provincial antiguo (1971-2000:
 * 1-2 letras + 4 dígitos + 1-2 letras, con separadores opcionales).
 * Acepta mayúsculas o minúsculas. La normalización final la hace el
 * backend (`VehiculoService`), que también detecta el formato y lo
 * persiste en `formatoMatricula`.
 */
const MATRICULA_TRAILER = /^(?:[0-9]{4}[A-Z]{3}|[A-Z]{1,2}[0-9]{4}[A-Z]{1,2})$/i;

/**
 * Valida la matrícula del remolque. Acepta:
 * - Formato nuevo: R + 4 dígitos + 3 letras (con/sin guiones).
 * - Formato antiguo con prefijo R-: R + 1-2 letras + 4 dígitos + 1-2 letras.
 * - Formato antiguo SIN prefijo R (caso real en remolques viejos):
 *   1-2 letras + 4 dígitos + 1-2 letras.
 */
const MATRICULA_REMOLQUE_RE = /^(?:R[0-9]{4}[A-Z]{3}|R?[A-Z]{1,2}[0-9]{4}[A-Z]{1,2})$/i;

function stripSeparators(s: string): string {
  return s.replace(/[\s-]/g, '').toUpperCase();
}

interface FormValues {
  matricula: string;
  matriculaRemolque: string;
  marca: string;
  modelo: string;
  tara: number | '';
  mma: number | '';
}

const initialFormValues: FormValues = {
  matricula: '',
  matriculaRemolque: '',
  marca: '',
  modelo: '',
  tara: '',
  mma: '',
};

export function VehiculosList() {
  const { data: vehiculos = [], isLoading, error } = useVehiculos();
  const crear = useCreateVehiculo();
  const actualizar = useUpdateVehiculo();
  const eliminar = useDeleteVehiculo();
  const [opened, { open, close }] = useDisclosure(false);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Vehiculo | null>(null);

  const filtered = vehiculos.filter((v) =>
    [v.matricula, v.matriculaRemolque, v.marca, v.modelo]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
    validate: {
      matricula: (v) => {
        const t = v.trim();
        if (!t) return 'Requerido';
        return MATRICULA_TRAILER.test(stripSeparators(t))
          ? null
          : 'Formato inválido. Usa NNNN-LLL (nuevo, ej. 1234-ABC) o LL NNNN LL (antiguo, ej. M 1234 AB).';
      },
      matriculaRemolque: (v) => {
        const t = v.trim();
        if (!t) return null;
        return MATRICULA_REMOLQUE_RE.test(stripSeparators(t))
          ? null
          : 'Formato inválido. Usa R-NNNN-LLL (nuevo), R LL NNNN LL (antiguo con R-) o LL NNNN LL (antiguo sin R-).';
      },
    },
  });

  useEffect(() => {
    if (!opened) return;
    if (editingId) {
      const v = vehiculos.find((x) => x.id === editingId);
      if (v) {
        form.setValues({
          matricula: v.matricula ?? '',
          matriculaRemolque: v.matriculaRemolque ?? '',
          marca: v.marca ?? '',
          modelo: v.modelo ?? '',
          tara: v.tara ?? '',
          mma: v.mma ?? '',
        });
        form.resetDirty();
      }
    } else {
      form.setValues(initialFormValues);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editingId]);

  const handleNew = () => {
    setEditingId(null);
    open();
  };

  const handleEdit = (v: Vehiculo) => {
    setEditingId(v.id);
    open();
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    eliminar.mutate(toDelete.id, {
      onSuccess: () => {
        notifications.show({
          title: 'Vehículo eliminado',
          message: `${toDelete.matricula} ya no aparece en el catalogo.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setToDelete(null);
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al eliminar',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const handleToggleActivo = (v: Vehiculo) => {
    actualizar.mutate(
      { id: v.id, data: { activo: !v.activo } },
      {
        onSuccess: () =>
          notifications.show({
            title: v.activo ? 'Vehículo desactivado' : 'Vehículo reactivado',
            message: v.matricula,
            color: 'green',
            icon: <IconCheck size={18} />,
          }),
        onError: (err: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(err),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const handleSubmit = (values: FormValues) => {
    // Normalizamos la matricula quitando separadores y mandamos el payload
    // tipado al backend.
    const remTrim = values.matriculaRemolque.trim();
    const basePayload = {
      matricula: stripSeparators(values.matricula),
      ...(remTrim ? { matriculaRemolque: stripSeparators(remTrim) } : {}),
      marca: values.marca.trim() || undefined,
      modelo: values.modelo.trim() || undefined,
      tara: typeof values.tara === 'number' && values.tara > 0 ? values.tara : undefined,
      mma: typeof values.mma === 'number' && values.mma > 0 ? values.mma : undefined,
    };

    if (editingId) {
      const updateData: UpdateVehiculoDto = basePayload;
      actualizar.mutate(
        { id: editingId, data: updateData },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Vehículo actualizado',
              message: `${values.matricula} guardado correctamente.`,
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            close();
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
      return;
    }

    const payload: CreateVehiculoDto = basePayload;
    crear.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Vehículo dado de alta',
          message: `${values.matricula} ya está disponible para asignar a envíos.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        close();
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al dar de alta',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Vehículos"
        subtitle="Cabeza tractora + remolque opcional. Doble matricula detectada automaticamente."
        icon={IconTruck}
        newLabel="Nuevo vehículo"
        count={vehiculos.length}
        onNew={handleNew}
        filters={
          <TextInput
            placeholder="Buscar por matrícula, marca o modelo…"
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={280}
          />
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los vehículos"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Matrícula</Table.Th>
              <Table.Th>Marca</Table.Th>
              <Table.Th>Modelo</Table.Th>
              <Table.Th>Tara (kg)</Table.Th>
              <Table.Th>MMA (kg)</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={7}>Cargando…</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>No hay vehículos. Da de alta el primero.</Table.Td>
              </Table.Tr>
            )}
            {filtered.map((v) => (
              <Table.Tr key={v.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" ff="monospace">
                        {v.matricula}
                      </Text>
                      {v.formatoMatricula === 'antiguo' && (
                        <Tooltip label="Matrícula provincial antigua (1971-2000)" withArrow>
                          <Badge size="xs" variant="light" color="orange">
                            Antiguo
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                    {v.matriculaRemolque && (
                      <Group gap={4} align="center">
                        <IconTruck size={12} style={{ opacity: 0.6 }} />
                        <Text size="xs" c="dimmed" component="span">
                          {v.matricula} (<code style={{ fontSize: 11 }}>{v.matriculaRemolque}</code>
                          )
                        </Text>
                        {v.formatoMatriculaRemolque === 'antiguo' && (
                          <Badge size="xs" variant="light" color="orange">
                            Antiguo
                          </Badge>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>{v.marca ?? '—'}</Table.Td>
                <Table.Td>{v.modelo ?? '—'}</Table.Td>
                <Table.Td>{v.tara ?? '—'}</Table.Td>
                <Table.Td>{v.mma ?? '—'}</Table.Td>
                <Table.Td>
                  <StatusBadge activo={v.activo} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Menu position="bottom-end" withinPortal shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => handleEdit(v)}
                      >
                        Editar
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPower size={14} />}
                        onClick={() => handleToggleActivo(v)}
                      >
                        {v.activo ? 'Desactivar' : 'Reactivar'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => setToDelete(v)}
                      >
                        Eliminar
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={opened}
        onClose={close}
        title={editingId ? 'Editar vehículo' : 'Nuevo vehículo'}
        centered
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Matrícula"
              description="Cabeza tractora. Formato NNNN-LLL (nuevo, ej. 1234-ABC) o LL NNNN LL (antiguo provincial, ej. M 1234 AB)."
              placeholder="1234ABC o M 1234 AB"
              required
              {...form.getInputProps('matricula')}
            />
            <TextInput
              label="Matrícula remolque"
              description="Opcional. Para conjuntos articulados. Formato R-NNNN-LLL (nuevo), R LL NNNN LL (antiguo con R-) o LL NNNN LL (antiguo sin R-)."
              placeholder="R-0001-BC o M 1234 AB"
              {...form.getInputProps('matriculaRemolque')}
            />
            <Group grow>
              <TextInput label="Marca" placeholder="Iveco" {...form.getInputProps('marca')} />
              <TextInput
                label="Modelo"
                placeholder="Daily 35S14"
                {...form.getInputProps('modelo')}
              />
            </Group>
            <Group grow>
              <NumberInput
                label="Tara (kg)"
                min={0}
                value={form.values.tara}
                onChange={(value) =>
                  form.setFieldValue('tara', typeof value === 'number' ? value : '')
                }
                error={form.errors.tara as string | undefined}
              />
              <NumberInput
                label="MMA (kg)"
                min={0}
                value={form.values.mma}
                onChange={(value) =>
                  form.setFieldValue('mma', typeof value === 'number' ? value : '')
                }
                error={form.errors.mma as string | undefined}
              />
            </Group>
            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={close}
                disabled={crear.isPending || actualizar.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={crear.isPending || actualizar.isPending}>
                {editingId ? 'Guardar cambios' : 'Dar de alta'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmDialog
        opened={toDelete !== null}
        title="Eliminar vehículo"
        message={
          toDelete ? `¿Eliminar ${toDelete.matricula}? Esta accion no se puede deshacer.` : ''
        }
        confirmLabel="Eliminar"
        confirmColor="red"
        loading={eliminar.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Stack>
  );
}
